/**
 * Submits every `recover_customer_password` form (the login drawer's and /account/login's) in
 * place instead of as a full page POST.
 *
 * A native submit gives the shopper no useful feedback: Shopify answers `/account/recover` with a
 * redirect to `/account/login`, and when it throttles the request it answers 429 with its own
 * "Something went wrong" page. This posts the same fields with fetch and writes the outcome into
 * the form's `[data-recover-status]` element, rendered by snippets/recover-status.liquid (styles
 * in assets/login-recover.css).
 *
 * Shopify's spam protection (the `captcha-bootstrap` script in content_for_header, when hCaptcha
 * is on for customer forms) protects this form too. On the form's first focus it binds hCaptcha,
 * which captures the form's `submit` method at that moment; on submit it fetches a token, appends
 * it as `h-captcha-response` and calls the captured method - a full page POST, regardless of our
 * preventDefault. So the form gets our own `submit` before that binding (on focusin, in the
 * capture phase, which runs before the bootstrap's own focusin listener): hCaptcha then hands its
 * tokenised submit to send(), and a protected form is never sent twice or without its token.
 *
 * The drawer's HTML is fetched/restored from sessionStorage after page load, so everything here
 * listens on the document, in the capture phase. Without fetch it leaves the native submit alone.
 */
(function () {
  'use strict';

  if (window.SavvyLoginRecover) return;
  window.SavvyLoginRecover = true;

  // Copy used when the form's HTML predates the status element (the drawer's is cached for hours).
  var FALLBACK_MESSAGES = {
    sent: 'Si existe una cuenta con ese correo, te enviamos un enlace para restablecer tu contraseña. Puede tardar unos minutos en llegar.',
    throttled: 'Demasiados intentos. Espera unos minutos e inténtalo de nuevo.',
    failed: 'No pudimos enviar la solicitud. Inténtalo de nuevo.'
  };

  var SUPPORTED = !!(window.fetch && window.URLSearchParams);

  // How long the "sent" message stays before it clears itself.
  var SENT_VISIBLE_MS = 8000;

  function isRecoverForm(form) {
    return form instanceof HTMLFormElement &&
      form.elements.form_type &&
      form.elements.form_type.value === 'recover_customer_password';
  }

  // Flags Shopify's captcha-bootstrap / hCaptcha set on a form they protect.
  function isCaptchaProtected(form) {
    var data = form.dataset;
    return !!(data.cptcha || data.hcaptchaBound || data.recaptchaBound);
  }

  function statusElement(form) {
    var el = form.querySelector('[data-recover-status]');
    if (el) return el;
    el = document.createElement('p');
    el.setAttribute('data-recover-status', '');
    el.setAttribute('role', 'status');
    el.className = 'sv-recover-status t4s_mb_20';
    var button = form.querySelector('button');
    form.insertBefore(el, button ? button.closest('.t4s_field') || button : null);
    return el;
  }

  function showStatus(form, state) {
    var el = statusElement(form);
    clearTimeout(el.svHideTimer);
    el.textContent = el.getAttribute('data-msg-' + state) || FALLBACK_MESSAGES[state];
    el.className = el.className.replace(/\s*sv-recover-status--\w+/g, '') + ' sv-recover-status--' + state;
    el.hidden = false;
    // Errors stay until the shopper acts; the success message only needs to be read once.
    if (state === 'sent') el.svHideTimer = setTimeout(function () { hideStatus(form); }, SENT_VISIBLE_MS);
  }

  function hideStatus(form) {
    var el = form.querySelector('[data-recover-status]');
    if (!el) return;
    clearTimeout(el.svHideTimer);
    el.hidden = true;
    el.textContent = '';
    el.className = el.className.replace(/\s*sv-recover-status--\w+/g, '');
  }

  function send(form) {
    var button = form.querySelector('button');
    if (button) {
      if (button.disabled) return;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    fetch(form.action, {
      method: 'POST',
      credentials: 'same-origin',
      // Shopify answers an accepted request with a 302 to the shop's *primary* domain, which is
      // cross-origin on any other host (myshopify.com, theme previews, `theme dev`). Following it
      // fails CORS after the email was already sent, so the redirect itself is the success signal.
      redirect: 'manual',
      // Includes `h-captcha-response` when hCaptcha added it before calling form.submit().
      body: new URLSearchParams(new FormData(form))
    })
      .then(function (response) {
        if (response.status === 429) return showStatus(form, 'throttled');
        if (response.type !== 'opaqueredirect' && !response.ok) return showStatus(form, 'failed');
        // Shopify doesn't say whether the email has an account, so neither does this message.
        form.elements.email.value = '';
        showStatus(form, 'sent');
      })
      .catch(function () {
        showStatus(form, 'failed');
      })
      .then(function () {
        if (!button) return;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      });
  }

  // Routes form.submit() - including the one hCaptcha captures when it binds - through send().
  function adopt(form) {
    if (form.svRecoverAdopted) return;
    form.svRecoverAdopted = true;
    try {
      form.submit = function () { send(form); };
    } catch (e) { /* non-writable: hCaptcha falls back to the native submit */ }
  }

  document.addEventListener('focusin', function (event) {
    var form = event.target && event.target.form;
    if (SUPPORTED && isRecoverForm(form)) adopt(form);
  }, true);

  // Editing the email again makes any earlier outcome stale.
  document.addEventListener('input', function (event) {
    var form = event.target && event.target.form;
    if (isRecoverForm(form) && event.target.name === 'email') hideStatus(form);
  }, true);

  // Closing the login drawer (X, overlay or Esc) or switching it back to the login panel sets
  // aria-hidden="true" on the drawer / panel (T4SThemeSP.Drawer, T4SThemeSP.Login). Clear the
  // outcome then, so it isn't still showing the next time the form is opened.
  new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      var target = mutation.target;
      if (target.getAttribute('aria-hidden') !== 'true') return;
      Array.prototype.forEach.call(target.querySelectorAll('form'), function (form) {
        if (isRecoverForm(form)) hideStatus(form);
      });
    });
  }).observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['aria-hidden'] });

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!SUPPORTED || !isRecoverForm(form)) return;
    event.preventDefault();
    adopt(form);
    // A protected form is sent by hCaptcha's own submit listener, once it has a token.
    if (!isCaptchaProtected(form)) send(form);
  }, true);
})();
